// src/pages/ListaEgresos.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { db, storage } from '../firebase';
import { collection, query, orderBy, onSnapshot, addDoc, doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import ModalRegistroEgreso from '../components/ModalRegistroEgreso';
import ModalEditarEgreso from '../components/ModalEditarEgreso';
import ConfirmationModal from '../components/ConfirmationModal';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import ActionMenu from '../components/ActionMenu';
import { registerMovement } from '../utils/registerMovement';
import * as XLSX from 'xlsx';
import './GenerarTicket.css';
import './ListaIngresos.css';
import './ConfiguracionRecibos.css';

const ListaEgresos = () => {
    const [egresos, setEgresos] = useState([]);
    const [loading, setLoading] = useState(true);
    const { userData, currentUser } = useAuth();
    const fraccionamientoId = userData?.fraccionamientoId;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showReceipt, setShowReceipt] = useState(false);
    const [egresoSeleccionado, setEgresoSeleccionado] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [egresoEditTarget, setEgresoEditTarget] = useState(null);
    const receiptRef = useRef(null);
    const [receiptConfig, setReceiptConfig] = useState(null);
    const [confirmationModal, setConfirmationModal] = useState({
        open: false,
        title: '',
        message: '',
        type: 'info',
    });
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, egresoId: null, isLoading: false });
    const navigate = useNavigate();

    // Filtros básicos para replicar la vista de ingresos (sin columna de casa)
    const [filtroConcepto, setFiltroConcepto] = useState('');
    const [filtroFecha, setFiltroFecha] = useState('');
    const [filtroProveedor, setFiltroProveedor] = useState('');

    // Paginación
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 12;

    useEffect(() => {
        if (!fraccionamientoId) return;
        const ref = collection(db, "fraccionamientos", fraccionamientoId, "egresos");
        const q = query(ref, orderBy('fecha', 'desc'));
        const unsub = onSnapshot(q, (snapshot) => {
            // Filtrar eliminados en el cliente
            const list = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(doc => !doc.deleted);
            setEgresos(list);
            setLoading(false);
            // Scroll al inicio cuando los datos terminan de cargar
            setTimeout(() => {
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            }, 100);
        });
        return () => unsub();
    }, [fraccionamientoId]);

    useEffect(() => {
        const loadReceiptConfig = async () => {
            if (!fraccionamientoId) return;
            try {
                const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
                const snap = await getDoc(fraccRef);
                if (snap.exists()) {
                    setReceiptConfig(snap.data().configuracionRecibos || null);
                }
            } catch (err) {
                console.error('ListaEgresos: no se pudo cargar configuracionRecibos', err);
            }
        };
        loadReceiptConfig();
    }, [fraccionamientoId]);

    const formatCurrency = (amount) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount || 0);

    const handleConfirmEgreso = async (datosEgreso) => {
        if (!fraccionamientoId) return alert("Error: No se pudo identificar el fraccionamiento.");

        const {
            monto,
            fechaEgreso,
            categoria,
            descripcion,
            comprobante,
            proveedor,
            referenciaFactura,
            referencia,
            folio,
            metodoPago,
            descuentoPorcentaje,
            prioridad,
            tipoGasto,
            compensacionBolsaCongelada,
        } = datosEgreso;
        let downloadURL = null;

        const cfg = receiptConfig || {};
        const serieEgreso = (cfg.folioSerieEgreso || cfg.folioSerie || 'EG').replace(/[^A-Za-z]/g, '') || 'EG';
        const baseConsecutivo = cfg.folioConsecutivoEgreso || cfg.folioConsecutivo || 1;
        const nextConsecutivo = baseConsecutivo + (egresos?.length || 0);
        const folioGenerado = folio && folio.trim()
            ? folio.trim()
            : `${serieEgreso}${String(nextConsecutivo).padStart(6, '0')}`;

        // Normalizamos la fecha al mediodía para evitar desfases por zona horaria
        const fechaEgresoDate = (() => {
            const baseDate = fechaEgreso ? new Date(fechaEgreso) : new Date();
            if (Number.isNaN(baseDate.getTime())) {
                const fallback = new Date();
                fallback.setHours(12, 0, 0, 0);
                return fallback;
            }
            baseDate.setHours(12, 0, 0, 0);
            return baseDate;
        })();

        try {
            if (comprobante) {
                const storageRef = ref(storage, `comprobantes_egresos/${fraccionamientoId}/${Date.now()}_${comprobante.name}`);
                const snapshot = await uploadBytes(storageRef, comprobante);
                downloadURL = await getDownloadURL(snapshot.ref);
            }

            const egresosRef = collection(db, 'fraccionamientos', fraccionamientoId, 'egresos');
            const descuentoValor = Number(monto) * (Number(descuentoPorcentaje || 0) / 100);
            const montoNeto = Number(monto) - descuentoValor;
            await addDoc(egresosRef, {
                fraccionamientoId,
                monto: Number(monto),
                descuento: descuentoValor,
                descuentoPorcentaje: Number(descuentoPorcentaje || 0),
                montoNeto,
                fecha: fechaEgresoDate,
                timestamp: serverTimestamp(),
                categoria,
                descripcion,
                proveedor: proveedor || '',
                referenciaFactura: referenciaFactura || '',
                referencia: referencia || '',
                folio: folioGenerado,
                metodoPago: metodoPago || '',
                comprobanteURL: downloadURL,
                prioridad: prioridad,
                tipoGasto: tipoGasto || 'Variable',
                "Tipo de Gasto:": tipoGasto || 'Variable',
                compensacionBolsaCongelada: compensacionBolsaCongelada || null,
            });

            // Si se compensó faltante desde una bolsa con fondos congelados, actualizar el saldo congelado acumulado en la categoría donante
            if (compensacionBolsaCongelada?.activa && compensacionBolsaCongelada.bolsaDonanteId) {
                try {
                    const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
                    const fraccSnap = await getDoc(fraccRef);
                    if (fraccSnap.exists()) {
                        const currentCfg = fraccSnap.data().configuracionRecibos || {};
                        const cats = Array.isArray(currentCfg.categoriasEgreso) ? [...currentCfg.categoriasEgreso] : [];
                        const idxDonante = cats.findIndex(c => c.id === compensacionBolsaCongelada.bolsaDonanteId || c.nombre === compensacionBolsaCongelada.bolsaDonanteNombre);
                        if (idxDonante >= 0) {
                            const donante = cats[idxDonante];
                            const cong = donante.congelacion || {};
                            const saldoActual = Number(cong.saldoCongeladoAcumulado || 0);
                            const nuevoSaldo = Math.max(0, saldoActual - Number(compensacionBolsaCongelada.montoCompensado || 0));
                            const historico = Array.isArray(cong.historico) ? [...cong.historico] : [];
                            historico.push({
                                fecha: new Date().toISOString(),
                                tipo: 'compensacion_egreso',
                                monto: Number(compensacionBolsaCongelada.montoCompensado || 0),
                                egresoFolio: folioGenerado,
                                categoriaReceptora: categoria
                            });
                            cats[idxDonante] = {
                                ...donante,
                                congelacion: {
                                    ...cong,
                                    saldoCongeladoAcumulado: nuevoSaldo,
                                    historico
                                }
                            };
                            await updateDoc(fraccRef, {
                                "configuracionRecibos.categoriasEgreso": cats
                            });
                        }
                    }
                } catch (congErr) {
                    console.warn('ListaEgresos: error descontando saldo congelado de bolsa donante:', congErr);
                }
            }
            try {
                await registerMovement({
                    fraccionamientoId,
                    titulo: `Nuevo egreso registrado: ${categoria}`,
                    descripcion: `${descripcion} · ${formatCurrency(montoNeto || monto)}`,
                    categoria: 'Egresos',
                    tipoMovimiento: 'nuevo_egreso',
                    origen: 'finanzas',
                    metadata: {
                        monto: Number(monto),
                        descuento: descuentoValor,
                        descuentoPorcentaje: Number(descuentoPorcentaje || 0),
                        montoNeto,
                        categoriaEgreso: categoria,
                        metodoPago: metodoPago || null,
                        proveedor: proveedor || null,
                        referenciaFactura: referenciaFactura || null,
                        referencia: referencia || null,
                        folio: folioGenerado,
                        prioridad: prioridad || null,
                        tipoGasto: tipoGasto || 'Variable',
                        "Tipo de Gasto:": tipoGasto || 'Variable',
                        comprobanteURL: downloadURL || null,
                    },
                    userData,
                    currentUser,
                });
            } catch (logError) {
                console.error('ListaEgresos: error al registrar movimiento de egreso', logError);
            }
            setConfirmationModal({
                open: true,
                title: 'Egreso Registrado',
                message: `Se ha registrado exitosamente un egreso de ${formatCurrency(monto)} en la categoría ${categoria}.`,
                type: 'success',
            });
            setIsModalOpen(false);
        } catch (error) {
            console.error("Error al registrar el egreso:", error);
            setConfirmationModal({
                open: true,
                title: 'Error',
                message: 'Hubo un error al registrar el egreso. Intenta nuevamente.',
                type: 'error',
            });
        }
    };

    // Filtrado similar a ingresos
    const normalizar = (str = '') => str.toString().toLowerCase().trim();
    const conceptosConfig = useMemo(() => {
        if (!receiptConfig) return [];
        if (Array.isArray(receiptConfig.conceptosEgreso)) return receiptConfig.conceptosEgreso.filter(Boolean);
        if (Array.isArray(receiptConfig.conceptosEgresoList)) return receiptConfig.conceptosEgresoList.filter(Boolean);
        if (receiptConfig.conceptosEgreso && typeof receiptConfig.conceptosEgreso === 'object') {
            return Object.values(receiptConfig.conceptosEgreso).filter(Boolean);
        }
        return [];
    }, [receiptConfig]);

    const conceptosUnicos = [...new Set([
        ...conceptosConfig,
        ...egresos.map(eg => eg.categoria || eg.descripcion).filter(Boolean),
    ])];

    const egresosFiltrados = egresos.filter((egreso) => {
        const cumpleConcepto = !filtroConcepto || (egreso.categoria === filtroConcepto || egreso.descripcion === filtroConcepto);
        const cumpleProveedor = !filtroProveedor || normalizar(egreso.proveedor).includes(normalizar(filtroProveedor));
        const cumpleFecha = !filtroFecha || (
            egreso.fecha && egreso.fecha.toDate && egreso.fecha.toDate().toLocaleDateString('es-MX') === new Date(filtroFecha).toLocaleDateString('es-MX')
        );
        return cumpleConcepto && cumpleProveedor && cumpleFecha;
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [filtroConcepto, filtroProveedor, filtroFecha]);

    // Calcular índices para paginación
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = egresosFiltrados
        .slice()
        .sort((a, b) => {
            const da = (a.timestamp?.toDate ? a.timestamp.toDate() : (a.timestamp ? new Date(a.timestamp) : null))
                || (a.fecha?.toDate ? a.fecha.toDate() : (a.fecha ? new Date(a.fecha) : null))
                || new Date(0);
            const db = (b.timestamp?.toDate ? b.timestamp.toDate() : (b.timestamp ? new Date(b.timestamp) : null))
                || (b.fecha?.toDate ? b.fecha.toDate() : (b.fecha ? new Date(b.fecha) : null))
                || new Date(0);
            return db - da; // Orden descendente por fecha
        })
        .slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(egresosFiltrados.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        const tableElement = document.querySelector('.tabla-egresos');
        if (tableElement) {
            tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Exportar a Excel con los encabezados de ingresos (sin número de casa)
    const exportarExcel = () => {
        const datos = egresosFiltrados.map((egreso) => ({
            'Fecha': egreso.fecha ? egreso.fecha.toDate().toLocaleDateString('es-MX') : 'N/A',
            'Concepto': egreso.descripcion || egreso.categoria || 'N/A',
            'Proveedor': egreso.proveedor || egreso.proveedorNombre || 'N/A',
            'Método de Pago': egreso.metodoPago || 'N/A',
            'Referencia': egreso.referenciaFactura || egreso.referencia || 'N/A',
            'Monto': egreso.monto || 0,
            'Monto Neto': egreso.montoNeto || egreso.monto || 0,
        }));

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet([]);

        // Fila 1: Título descriptivo
        XLSX.utils.sheet_add_aoa(worksheet, [[
            'Reporte de Egresos - Historial completo de gastos y pagos realizados'
        ]], { origin: 'A1' });

        // Fila 2: Encabezados de columna y Fila 3+: Datos
        XLSX.utils.sheet_add_json(worksheet, datos, { origin: 'A2', skipHeader: false });

        XLSX.utils.book_append_sheet(workbook, worksheet, 'Egresos');

        worksheet['!cols'] = [
            { wch: 12 },  // Fecha
            { wch: 28 },  // Concepto
            { wch: 25 },  // Proveedor
            { wch: 18 },  // Método de Pago
            { wch: 18 },  // Referencia
            { wch: 12 },  // Monto
            { wch: 12 },  // Monto Neto
        ];

        // Fusionar celdas para el título (A1:G1)
        worksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }];

        XLSX.writeFile(workbook, `Historial_Egresos_${new Date().toLocaleDateString('es-MX')}.xlsx`);
    };

    const formatoFecha = (fecha) => {
        if (!fecha) return 'N/A';
        try {
            return fecha.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
        } catch (e) {
            return 'N/A';
        }
    };

    const buildFolioEgreso = (egreso) => {
        if (egreso.folio) return egreso.folio;
        const serie = (receiptConfig?.folioSerieEgreso || 'EG').replace(/[^A-Za-z]/g, '') || 'EG';
        const consec = receiptConfig?.folioConsecutivoEgreso || receiptConfig?.folioConsecutivo || 1;
        return `${serie}${String(consec).padStart(6, '0')}`;
    };

    const numeroALetras = (num = 0) => {
        const unidades = ['CERO', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
        const decenas = ['DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISÉIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
        const tens = ['', 'DIEZ', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
        const centenas = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];

        if (num === 0) return 'CERO';
        if (num === 100) return 'CIEN';

        const toWords = (n) => {
            let words = '';
            const c = Math.floor(n / 100);
            const d = Math.floor((n % 100) / 10);
            const u = n % 10;
            if (c) words += `${centenas[c]} `;
            if (d === 1) words += `${decenas[u]}`;
            else {
                if (d > 1) words += `${tens[d]}${u ? ' Y ' : ''}`;
                if (d !== 1 && u) words += unidades[u];
            }
            return words.trim();
        };

        const millones = Math.floor(num / 1000000);
        const miles = Math.floor((num % 1000000) / 1000);
        const resto = num % 1000;
        let res = '';
        if (millones) {
            if (millones === 1) res += 'UN MILLÓN';
            else res += `${toWords(millones)} MILLONES`;
        }
        if (miles) {
            res += res ? ' ' : '';
            if (miles === 1) res += 'MIL';
            else res += `${toWords(miles)} MIL`;
        }
        if (resto) {
            res += res ? ' ' : '';
            res += toWords(resto);
        }
        return res.trim();
    };

    const buildEgresoReceiptHtml = (egreso) => {
        const folio = buildFolioEgreso(egreso);
        const fechaPago = egreso.fecha?.toDate ? egreso.fecha.toDate() : (egreso.fecha ? new Date(egreso.fecha) : null);
        const fechaEmision = egreso.timestamp?.toDate ? egreso.timestamp.toDate() : new Date();
        const moneda = receiptConfig?.moneda || 'MXN';
        const formatCurrency = (val) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: moneda }).format(val || 0);
        const descuentoLinea = Number(egreso.descuentoPorcentaje || 0) > 0;
        const monedaEtiqueta = (moneda || 'MXN').toUpperCase() === 'USD' ? 'DÓLARES ESTADOUNIDENSES' : 'PESOS MEXICANOS';
        const totalNumero = Math.round(egreso.montoNeto || egreso.monto || 0);
        const totalEnLetras = `${numeroALetras(totalNumero)} ${monedaEtiqueta} 00/100`;

        return `<!doctype html>
<html><head><meta charset="UTF-8"><title>Recibo de Egreso</title>
<style>
body{font-family:Arial,sans-serif;background:#f5f6fb;padding:24px;color:#1f2937;}
.receipt{max-width:900px;margin:0 auto;background:#fff;border:1px solid #e5e7eb;border-radius:12px;box-shadow:0 12px 30px rgba(17,24,39,0.12);padding:24px;}
.header{display:flex;justify-content:space-between;gap:16px;align-items:flex-start;}
.logo{width:96px;height:96px;border:1px dashed #cbd5e1;border-radius:12px;display:flex;align-items:center;justify-content:center;font-weight:700;color:#94a3b8;overflow:hidden;}
.meta{text-align:right;}
.title{margin:0;font-size:20px;color:#111827;font-weight:800;}
.meta span{display:block;font-size:13px;color:#4b5563;margin-top:4px;}
.block{margin-top:18px;padding-top:12px;border-top:1px solid #e5e7eb;}
.block h4{margin:0 0 8px;font-size:14px;color:#111827;text-transform:uppercase;letter-spacing:0.5px;}
.table{width:100%;border-collapse:collapse;margin-top:6px;}
.table th,.table td{border:1px solid #e5e7eb;padding:10px;font-size:13px;text-align:left;}
.table th{text-transform:uppercase;font-size:12px;letter-spacing:0.5px;background:#f8fafc;color:#1f2937;}
.totals{display:flex;justify-content:space-between;align-items:flex-end;margin-top:14px;font-size:15px;font-weight:700;color:#111827;border-top:1px solid #e5e7eb;padding-top:10px;}
.totals .muted{font-weight:700;color:#111827;}
.footer{display:flex;flex-direction:column;align-items:center;justify-content:center;margin-top:32px;gap:12px;}
.sello{width:140px;height:140px;border:1px dashed #cbd5e1;border-radius:12px;display:flex;align-items:center;justify-content:center;color:#94a3b8;overflow:hidden;}
.firma{min-width:220px;text-align:center;}
.firma-line{border-top:1px solid #cbd5e1;margin:0 auto 6px;width:100%;}
.muted{color:#6b7280;font-size:12px;}
.leyenda{margin-top:8px;font-size:12px;color:#374151;text-align:left;}
</style></head><body><div class="receipt">
<div class="header">
    <div style="display:flex;gap:14px;align-items:center;">
        <div class="logo">${receiptConfig?.logoUrl ? `<img src="${receiptConfig.logoUrl}" alt="logo" style="width:100%;height:100%;object-fit:contain;"/>` : 'Logo'}</div>
        <div>
            <div style="font-weight:800;font-size:18px;">${receiptConfig?.condominioNombre || 'Residencial / Condominio'}</div>
            <div class="muted">${receiptConfig?.condominioDireccion || 'Dirección del condominio'}</div>
        </div>
    </div>
    <div class="meta">
        <div class="title">RECIBO DE EGRESO</div>
        <span>Folio: ${folio}</span>
        <span>Fecha de egreso: ${formatoFecha(fechaPago)}</span>
        <span>Fecha de emisión: ${formatoFecha(fechaEmision)} ${fechaEmision.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</span>
    </div>
</div>

<div class="block">
    <h4>Datos de Facturación</h4>
    <div><strong>Razón Social:</strong> ${receiptConfig?.razonSocial || 'Condominio / Razón Social'}</div>
    <div><strong>RFC:</strong> ${receiptConfig?.rfc || 'RFC123456ABC'}</div>
    ${receiptConfig?.datosFacturacion ? `<div class="muted">${receiptConfig.datosFacturacion}</div>` : ''}
</div>

<div class="block">
    <h4>Administrador</h4>
    <div><strong>Nombre:</strong> ${receiptConfig?.administradorNombre || 'Administrador'}</div>
    <div><strong>Dirección:</strong> ${receiptConfig?.adminDireccion || 'Dirección del administrador'}</div>
</div>

<div class="block">
    <h4>Proveedor</h4>
    <div><strong>Nombre:</strong> ${egreso.proveedor || 'Proveedor'}</div>
    <div><strong>Referencia / Factura:</strong> ${egreso.referenciaFactura || egreso.referencia || 'N/A'}</div>
    ${egreso.metodoPago ? `<div><strong>Método de Pago:</strong> ${egreso.metodoPago}</div>` : ''}
    ${egreso.prioridad ? `<div><strong>Prioridad:</strong> ${egreso.prioridad}</div>` : ''}
</div>

<div class="block">
    <h4>Conceptos de Egreso</h4>
    <table class="table">
        <thead><tr><th>Concepto</th><th>Importe</th></tr></thead>
        <tbody>
            <tr><td>${egreso.descripcion || egreso.categoria || 'Concepto'}</td><td>${formatCurrency(egreso.monto)}</td></tr>
            ${descuentoLinea ? `<tr><td>Descuento (${egreso.descuentoPorcentaje}%)</td><td>-${formatCurrency(egreso.descuento || 0)}</td></tr>` : ''}
        </tbody>
    </table>
</div>

<div class="totals">
    <span class="muted">${descuentoLinea ? 'TOTAL NETO' : 'TOTAL'}</span>
    <span>${formatCurrency(egreso.montoNeto || egreso.monto)}</span>
</div>

<div class="leyenda">SON: ${totalEnLetras}</div>

<div class="footer">
    <div class="sello">${receiptConfig?.selloUrl ? `<img src="${receiptConfig.selloUrl}" alt="sello" style="width:100%;height:100%;object-fit:contain;"/>` : 'Sello / Firma'}</div>
    <div class="firma">
        <div class="firma-line"></div>
        <div class="muted">Firma y Sello</div>
        <div>${receiptConfig?.administradorNombre || 'Administrador'}</div>
    </div>
</div>
</div>
</body></html>`;
    };

    const openReceiptWindow = (html, doPrint = false) => {
        // Usa Blob para asegurar que el navegador renderice el HTML completo
        const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const win = window.open(url, '_blank', 'noopener,noreferrer');
        if (!win) {
            alert('No se pudo abrir la ventana del recibo. Revisa el bloqueador de ventanas emergentes.');
            URL.revokeObjectURL(url);
            return;
        }
        win.focus();
        // Si se desea imprimir, esperar a que cargue el documento
        if (doPrint) {
            win.onload = () => {
                setTimeout(() => {
                    try { win.print(); } catch (_) { /* noop */ }
                }, 300);
            };
        }
        // Liberar el objeto cuando la ventana se cierre
        win.onbeforeunload = () => URL.revokeObjectURL(url);
    };

    const handleVerRecibo = (egreso) => {
        setEgresoSeleccionado(egreso);
        setShowReceipt(true);
    };

    const handleDownloadPdf = (egreso) => {
        try {
            const html = buildEgresoReceiptHtml(egreso);
            openReceiptWindow(html, false);
        } catch (e) {
            console.error('Error generando recibo de egreso:', e);
            alert('No se pudo generar el recibo. Intenta de nuevo.');
        }
    };

    const openEditModal = (egreso) => {
        setEgresoEditTarget(egreso);
        setShowEditModal(true);
    };

    const handleConfirmEditEgreso = async (updated) => {
        try {
            if (!fraccionamientoId || !egresoEditTarget?.id) return;
            const egresoRef = doc(db, 'fraccionamientos', fraccionamientoId, 'egresos', egresoEditTarget.id);
            let comprobanteURL = egresoEditTarget.comprobanteURL || null;
            if (updated.comprobante) {
                const storageRef = ref(storage, `comprobantes_egresos/${fraccionamientoId}/${Date.now()}_${updated.comprobante.name}`);
                const snapshot = await uploadBytes(storageRef, updated.comprobante);
                comprobanteURL = await getDownloadURL(snapshot.ref);
            }
            const monto = Number(updated.monto || 0);
            const descPct = Number(updated.descuentoPorcentaje || 0);
            const descuento = monto * (descPct / 100);
            const montoNeto = monto - descuento;
            await updateDoc(egresoRef, {
                monto,
                descuento,
                descuentoPorcentaje: descPct,
                montoNeto,
                metodoPago: updated.metodoPago || egresoEditTarget.metodoPago || '',
                fecha: updated.fecha || egresoEditTarget.fecha || new Date(),
                categoria: updated.categoria || egresoEditTarget.categoria || '',
                tipoGasto: updated.tipoGasto || egresoEditTarget.tipoGasto || 'Variable',
                "Tipo de Gasto:": updated.tipoGasto || egresoEditTarget.tipoGasto || 'Variable',
                descripcion: updated.descripcion || egresoEditTarget.descripcion || '',
                proveedor: updated.proveedor || egresoEditTarget.proveedor || '',
                referenciaFactura: updated.referenciaFactura || egresoEditTarget.referenciaFactura || '',
                referencia: updated.referencia || egresoEditTarget.referencia || '',
                prioridad: updated.prioridad || egresoEditTarget.prioridad || 'Media',
                comprobanteURL,
            });
            setShowEditModal(false);
            setEgresoEditTarget(null);
        } catch (err) {
            console.error('ListaEgresos: error al actualizar egreso', err);
            alert('No se pudo guardar el egreso. Intenta nuevamente.');
        }
    };

    const handleDeleteEgreso = (egresoId) => {
        setDeleteConfirm({ open: true, egresoId, isLoading: false });
    };

    const handleConfirmDeleteEgreso = async () => {
        const { egresoId } = deleteConfirm;
        if (!egresoId) return;

        setDeleteConfirm(prev => ({ ...prev, isLoading: true }));
        try {
            if (!fraccionamientoId) return;
            const egresoRef = doc(db, 'fraccionamientos', fraccionamientoId, 'egresos', egresoId);
            // Soft delete: marcar como eliminado en lugar de borrar
            await updateDoc(egresoRef, {
                deleted: true,
                deletedAt: serverTimestamp(),
                deletedReason: 'Eliminado por usuario'
            });
            setDeleteConfirm({ open: false, egresoId: null, isLoading: false });
        } catch (err) {
            console.error('ListaEgresos: error al eliminar egreso', err);
            alert('No se pudo eliminar el egreso. Intenta nuevamente.');
            setDeleteConfirm(prev => ({ ...prev, isLoading: false }));
        }
    };

    return (
        <div className="generar-ticket-page">
            <div className="lista-egresos-header">
                <button onClick={() => navigate('/finanzas')} className="btn-back-simple">← Volver</button>
                <h2>Historial de Egresos</h2>
                <div className="header-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button
                        className="btn-exportar-excel"
                        onClick={exportarExcel}
                        disabled={egresosFiltrados.length === 0}
                    >
                        📊 Exportar a Excel
                    </button>
                    <button onClick={() => setIsModalOpen(true)} className="btn-pagar" style={{ backgroundColor: '#d9534f' }}>+ Registrar Egreso</button>
                </div>
            </div>

            {/* Resumen */}
            <div className="ingresos-resumen ingresos-resumen-top">
                <div className="resumen-item">
                    <span>Total Registros:</span>
                    <strong>{egresosFiltrados.length}</strong>
                </div>
                <div className="resumen-item">
                    <span>Total Monto:</span>
                    <strong>{formatCurrency(egresosFiltrados.reduce((sum, eg) => sum + (eg.monto || 0), 0))}</strong>
                </div>
                <div className="resumen-item">
                    <span>Total Neto:</span>
                    <strong>{formatCurrency(egresosFiltrados.reduce((sum, eg) => sum + (eg.monto || 0), 0))}</strong>
                </div>
            </div>

            {/* Filtros */}
            <div className="filtros-container">
                <div className="filtro-grupo">
                    <label>Buscar por Concepto:</label>
                    <select value={filtroConcepto} onChange={(e) => setFiltroConcepto(e.target.value)}>
                        <option value="">Todos</option>
                        {conceptosUnicos.map((c) => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </select>
                </div>
                <div className="filtro-grupo">
                    <label>Buscar por Proveedor:</label>
                    <input
                        type="text"
                        value={filtroProveedor}
                        onChange={(e) => setFiltroProveedor(e.target.value)}
                        placeholder="Nombre o razón social"
                    />
                </div>
                <div className="filtro-grupo">
                    <label>Buscar por Fecha:</label>
                    <input
                        type="date"
                        value={filtroFecha}
                        onChange={(e) => setFiltroFecha(e.target.value)}
                    />
                </div>
                <button
                    className="btn-limpiar-filtros"
                    onClick={() => {
                        setFiltroConcepto('');
                        setFiltroFecha('');
                        setFiltroProveedor('');
                    }}
                >
                    🔄 Limpiar Filtros
                </button>
            </div>

            {/* Listado de Egresos */}
            {loading ? (
                <p>Cargando egresos...</p>
            ) : egresosFiltrados.length > 0 ? (
                <div className="tabla-ingresos tabla-egresos premium-table-container">
                    <table>
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>Concepto</th>
                                <th>Método de Pago</th>
                                <th>Referencia</th>
                                <th>Monto</th>
                                <th>Descuento</th>
                                <th>Neto</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.map((item) => {
                                const dateObj = (item.timestamp?.toDate ? item.timestamp.toDate() : (item.timestamp ? new Date(item.timestamp) : null))
                                    || (item.fecha?.toDate ? item.fecha.toDate() : (item.fecha ? new Date(item.fecha) : null));
                                const dateLabel = dateObj ? dateObj.toLocaleDateString('es-MX') : 'N/A';
                                const timeLabel = dateObj ? dateObj.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '';
                                return (
                                    <tr key={item.id}>
                                        <td>
                                            {dateLabel}
                                            {timeLabel ? <div style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: '2px' }}>{timeLabel}</div> : null}
                                        </td>
                                        <td className="texto-extendido">{item.descripcion || item.categoria || 'N/A'}</td>
                                        <td>{item.metodoPago || 'N/A'}</td>
                                        <td className="texto-extendido">{item.referenciaFactura || item.referencia || 'N/A'}</td>
                                        <td className="monto-ingreso">{formatCurrency(item.monto)}</td>
                                        <td className="descuento-info">{item.descuentoPorcentaje ? `${item.descuentoPorcentaje}% (-${formatCurrency(item.descuento || 0)})` : 'N/A'}</td>
                                        <td className="monto-neto">{formatCurrency(item.montoNeto || item.monto)}</td>
                                        <td>
                                            <ActionMenu
                                                onView={() => handleVerRecibo(item)}
                                                onEdit={() => openEditModal(item)}
                                                onDelete={() => handleDeleteEgreso(item.id)}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p>No se han registrado egresos todavía.</p>
            )}
            {/* Fin de listado */}

            {/* Paginación */}
            {totalPages > 1 ? (
                <div className="pagination-wrapper">
                    <div className="premium-pagination">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="pagination-btn"
                        >
                            &lsaquo; Anterior
                        </button>
                        <div className="pagination-info">Página {currentPage} de {totalPages}</div>
                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="pagination-btn"
                        >
                            Siguiente &rsaquo;
                        </button>
                    </div>
                </div>
            ) : (
                <div style={{ height: '40px' }}></div> // Espaciador si no hay paginación
            )}

            {/* RENDERIZADO DEL MODAL */}
            {
                isModalOpen && (
                    <ModalRegistroEgreso
                        onClose={() => setIsModalOpen(false)}
                        onConfirm={handleConfirmEgreso}
                        nextFolio={() => {
                            const cfg = receiptConfig || {};
                            const serieEgreso = (cfg.folioSerieEgreso || cfg.folioSerie || 'EG').replace(/[^A-Za-z]/g, '') || 'EG';
                            const baseConsecutivo = cfg.folioConsecutivoEgreso || cfg.folioConsecutivo || 1;
                            const nextConsecutivo = baseConsecutivo + (egresos?.length || 0);
                            return `${serieEgreso}${String(nextConsecutivo).padStart(6, '0')}`;
                        }}
                    />
                )
            }

            {
                showReceipt && egresoSeleccionado && (
                    <div className="modal-overlay receipt-modal">
                        <div className="modal-content receipt-modal-content">
                            <button className="modal-close-btn" onClick={() => setShowReceipt(false)}>✕</button>
                            <div className="receipt-scroll-area">
                                <div
                                    className="receipt-preview-container"
                                    ref={receiptRef}
                                    dangerouslySetInnerHTML={{ __html: buildEgresoReceiptHtml(egresoSeleccionado) }}
                                />
                            </div>
                            <div className="receipt-actions">
                                <button type="button" className="btn-secondary" onClick={() => handleDownloadPdf(egresoSeleccionado)}>
                                    ⬇️ Descargar / Imprimir
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {
                showEditModal && egresoEditTarget && (
                    <ModalEditarEgreso
                        egreso={egresoEditTarget}
                        onClose={() => { setShowEditModal(false); setEgresoEditTarget(null); }}
                        onConfirm={handleConfirmEditEgreso}
                    />
                )
            }

            <ConfirmationModal
                open={confirmationModal.open}
                title={confirmationModal.title}
                message={confirmationModal.message}
                type={confirmationModal.type}
                icon={confirmationModal.type === 'success' ? <FaCheckCircle /> : undefined}
                actionLabel="Aceptar"
                onClose={() => setConfirmationModal({ ...confirmationModal, open: false })}
            />

            <ConfirmDeleteModal
                isOpen={deleteConfirm.open}
                title="¿Eliminar egreso?"
                message="¿Estás seguro de que deseas eliminar este egreso? Esta acción no se puede deshacer."
                onConfirm={handleConfirmDeleteEgreso}
                onCancel={() => setDeleteConfirm({ open: false, egresoId: null, isLoading: false })}
                isLoading={deleteConfirm.isLoading}
            />
        </div >
    );
};
export default ListaEgresos;